import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog, LogOut } from 'lucide-react';
import { useAuthStore } from './store';
import { stopImpersonation } from './api';

/**
 * Sticky banner shown while a super_admin is impersonating another user. Makes
 * the impersonation obvious (so the admin doesn't forget which identity they're
 * acting as) and offers a one-click return to their own session.
 */
export function ImpersonationBanner() {
  const impersonator = useAuthStore((s) => s.impersonator);
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!impersonator) return null;

  const handleReturn = async () => {
    setBusy(true);
    try {
      const res = await stopImpersonation();
      setAuth({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
        user: res.user,
        impersonator: res.impersonator ?? null,
      });
      navigate('/');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '10px 16px',
        marginBottom: 16,
        borderRadius: 10,
        background: 'rgba(245, 158, 11, 0.12)',
        border: '1px solid rgba(245, 158, 11, 0.45)',
        color: '#f59e0b',
        fontSize: 13.5,
        fontWeight: 600,
      }}
    >
      <UserCog size={18} />
      <span style={{ color: 'var(--text)' }}>
        กำลังใช้งานในนาม{' '}
        <strong style={{ color: '#f59e0b' }}>{user?.fullName || user?.username}</strong>{' '}
        <span style={{ color: 'var(--dim)', fontWeight: 400 }}>
          (เข้าใช้แทนโดย {impersonator.username})
        </span>
      </span>
      <button
        onClick={handleReturn}
        disabled={busy}
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 14px',
          borderRadius: 8,
          border: '1px solid rgba(245, 158, 11, 0.6)',
          background: 'rgba(245, 158, 11, 0.18)',
          color: '#f59e0b',
          fontWeight: 700,
          fontFamily: 'inherit',
          fontSize: 13,
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <LogOut size={14} />
        กลับเป็น {impersonator.username}
      </button>
    </div>
  );
}
